import { createRoute, Outlet } from '@tanstack/react-router';
import { SmallScreenGate } from '@eureca/ui';
import { rootRoute } from '@/router/root-route';
import { OnboardingPage } from '@/features/job-description/onboarding/OnboardingPage';
import { StepPage } from '@/features/job-description/steps/StepPage';
import { SummaryPage } from '@/features/job-description/summary/SummaryPage';
import { SuccessPage } from '@/features/job-description/success/SuccessPage';
import { ConnectionGate } from '@/features/job-description/shared/ConnectionGate';
import { GraveErrorScreen } from '@/features/job-description/shared/SystemScreens';
import {
  ALIGNMENT_SMALL_SCREEN_QUERY,
  SMALL_SCREEN,
} from '@/features/job-description/shared/messages';
import { isAlignmentFormId } from '@/features/job-description/shared/public-path';

/**
 * DEV-258 — formulário da pessoa gestora, aberto pelo link
 * `consultor.eureca.{dev,me}/descricao-vaga/{tenant}/{formId}` (AC-08 do DEV-268).
 *
 * Rotas PÚBLICAS: filhas diretas da rootRoute, fora do `authenticatedRoute` e
 * sem `requireWorkspace` (mesmo desenho do `/compartilhado/$token`, EU-594). O
 * `main.tsx` não monta o KeycloakGate nesse prefixo. A porta é o token do link,
 * validado pelo BFF nas rotas `/public/alignment/*`.
 *
 * Link quebrado — sem tenant, `formId` que não é UUID, segmento a mais — cai na
 * tela do frame "404" do formulário, nunca no not-found do app do consultor (Q13).
 */
function BrokenLinkScreen() {
  return <GraveErrorScreen onRetry={() => window.location.reload()} />;
}

/**
 * Cada rota resolve o próprio fallback de erro (`errorComponent` da rota ou o
 * `defaultErrorComponent` do router, que é o do consultor, com menu). Toda rota
 * do formulário declara o dela para a pessoa gestora nunca ver o app do consultor.
 */
const PUBLIC_ERROR = { errorComponent: BrokenLinkScreen } as const;

function AlignmentLayout() {
  const { formId } = jobDescriptionRoute.useParams();
  if (!isAlignmentFormId(formId)) return <BrokenLinkScreen />;
  return (
    <SmallScreenGate
      preserveChildren
      query={ALIGNMENT_SMALL_SCREEN_QUERY}
      title={SMALL_SCREEN.title}
      body={SMALL_SCREEN.body}
    >
      <ConnectionGate>
        <Outlet />
      </ConnectionGate>
    </SmallScreenGate>
  );
}

export const jobDescriptionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/descricao-vaga/$tenant/$formId',
  component: AlignmentLayout,
  notFoundComponent: BrokenLinkScreen,
  ...PUBLIC_ERROR,
});

/** Qualquer outra forma dentro do prefixo (`/descricao-vaga`, `/descricao-vaga/acme`). */
export const jobDescriptionBrokenLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/descricao-vaga/$',
  component: BrokenLinkScreen,
});

function OnboardingRoute() {
  const { tenant, formId } = jobDescriptionRoute.useParams();
  return <OnboardingPage tenant={tenant} formId={formId} />;
}

export const jobDescriptionOnboardingRoute = createRoute({
  getParentRoute: () => jobDescriptionRoute,
  path: '/',
  component: OnboardingRoute,
  ...PUBLIC_ERROR,
});

interface StepSearch {
  /** "Editar" do Resumo: o Avançar devolve direto para lá (DEV-261). */
  from?: 'resumo';
}

function StepRoute() {
  const { tenant, formId, pageName } = jobDescriptionStepRoute.useParams();
  const { from } = jobDescriptionStepRoute.useSearch();
  return (
    <StepPage
      tenant={tenant}
      formId={formId}
      pageName={pageName}
      returnToSummary={from === 'resumo'}
    />
  );
}

export const jobDescriptionStepRoute = createRoute({
  getParentRoute: () => jobDescriptionRoute,
  path: 'etapa/$pageName',
  validateSearch: (search: Record<string, unknown>): StepSearch =>
    search.from === 'resumo' ? { from: 'resumo' } : {},
  component: StepRoute,
  ...PUBLIC_ERROR,
});

function SummaryRoute() {
  const { tenant, formId } = jobDescriptionRoute.useParams();
  return <SummaryPage tenant={tenant} formId={formId} />;
}

export const jobDescriptionSummaryRoute = createRoute({
  getParentRoute: () => jobDescriptionRoute,
  path: 'resumo',
  component: SummaryRoute,
  ...PUBLIC_ERROR,
});

function SuccessRoute() {
  const { tenant, formId } = jobDescriptionRoute.useParams();
  return <SuccessPage tenant={tenant} formId={formId} />;
}

export const jobDescriptionSuccessRoute = createRoute({
  getParentRoute: () => jobDescriptionRoute,
  path: 'sucesso',
  component: SuccessRoute,
  ...PUBLIC_ERROR,
});

export const jobDescriptionRoutes = [
  jobDescriptionRoute.addChildren([
    jobDescriptionOnboardingRoute,
    jobDescriptionStepRoute,
    jobDescriptionSummaryRoute,
    jobDescriptionSuccessRoute,
  ]),
  jobDescriptionBrokenLinkRoute,
];
